import { asyncWithLDProvider, initialize } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FEATURE_FLAG_DEFAULTS } from '@/constants/feature-flagging';
import LoadingSpinner from 'components/LoadingSpinner/Loadable';
import { loadUser } from 'containers/App/actions';
import { UI_TYPES } from 'containers/App/constants';
import appReducer from 'containers/App/reducer';
import appSaga from 'containers/App/saga';
import {
  makeSelectLoadingUser,
  makeSelectUser,
} from 'containers/App/selectors';
import accessTokenService from 'utils/accessTokenService';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectSaga } from 'utils/injectSaga';

// Rendered in place of the real LD provider when initialization fails, so the
// app degrades gracefully (children render, flags resolve to in-code defaults)
// instead of hanging forever on the loading spinner.
const PassthroughProvider = ({ children }) => children;
PassthroughProvider.propTypes = { children: PropTypes.node };

/**
 * Initializes LaunchDarkly ONLY after a real authenticated user is known.
 *
 * Previously the SDK was initialized with no context, so it created an
 * anonymous context (= a billable MAU) on every load/HMR/test run. That was
 * ~99% of our MAU. Initializing with the real user.uuid means no anonymous
 * context is ever created, and the same key is reused across sessions.
 */
const LaunchDarklyProvider = ({ children }) => {
  // We read app state and dispatch loadUser above <App/>, so inject its
  // reducer/saga here too. These are keyed + idempotent, so App's own
  // injects remain harmless no-ops.
  useInjectReducer({ key: 'app', reducer: appReducer });
  useInjectSaga({ key: 'appSaga', saga: appSaga });

  const dispatch = useDispatch();
  const user = useSelector(makeSelectUser());
  const isLoadingUser = useSelector(makeSelectLoadingUser());

  const hasToken = !!accessTokenService.getAccessToken();

  const [LDProvider, setLDProvider] = useState(null);
  // Only re-init when the user KEY itself changes (login / switch user) —
  // never on attribute/org changes.
  const initializedKey = useRef(null);
  // Handle to the JS SDK client we create. We own its lifecycle so we can
  // close() it (tearing down its streaming connection) when the user key
  // changes or this provider unmounts — otherwise a different user logging in
  // within the same tab would leave the previous client streaming forever.
  const ldClientRef = useRef(null);
  const attemptedLoadUser = useRef(false);

  // Fetch the user as early as possible (moved up from <App/>). Deliberately
  // NOT gated on the sessionStorage token: after returning from the ID portal
  // (and in every new tab — sessionStorage is per-tab) no token exists yet.
  // It is minted by the api layer's refresh interceptor (SSO cookie) during
  // this very request, and sessionStorage writes never re-render React, so a
  // token gate here froze the provider logged-out — no LD client, all flags
  // at defaults — until a manual reload. For a genuinely logged-out visitor
  // the request 401s, user stays null, and PrivateRoute redirects to the ID
  // portal exactly as before. Fired once per mount, like <App/> used to.
  useEffect(() => {
    if (!user && !isLoadingUser && !attemptedLoadUser.current) {
      attemptedLoadUser.current = true;
      dispatch(loadUser());
    }
  }, [user, isLoadingUser, dispatch]);

  // Build the provider once we have a real user key.
  useEffect(() => {
    const userKey = user?.uuid || null;
    // Logged out / user cleared in this tab: tear down the previous user's
    // client and provider. This provider never unmounts (it sits at the top of
    // the tree), so without this reset the old user's streaming connection
    // keeps running, and a different user logging in the same tab would briefly
    // be wrapped in the stale provider before their own init completes.
    if (!userKey) {
      if (ldClientRef.current) {
        ldClientRef.current.close();
        ldClientRef.current = null;
      }
      initializedKey.current = null;
      setLDProvider(null);
      return undefined;
    }

    if (initializedKey.current === userKey) return undefined;

    let cancelled = false;
    initializedKey.current = userKey;

    // Tear down any client from a previous user key before creating a new one
    // so we never leave an orphaned streaming connection open.
    if (ldClientRef.current) {
      ldClientRef.current.close();
      ldClientRef.current = null;
    }

    (async () => {
      try {
        // Initialize the client ourselves (rather than letting
        // asyncWithLDProvider do it) so we hold a reference we can close().
        // Same (legacy) user shape App.js already passes to identify(), so
        // existing targeting rules keep working unchanged.
        const ldClient = initialize(
          process.env.DARKLY_KEY,
          {
            key: userKey,
            name: `${user.firstName} ${user.lastName}`,
            email: user.email,
            custom: {
              ui: UI_TYPES.LIGHT_UI,
              intercom: true,
              orgResourceId: null, // set later via identify() once org is known
            },
            privateAttributeNames: ['email'],
          },
          // Mirror the options asyncWithLDProvider applies internally.
          // bootstrap from the previous session's cached flags so the ready
          // event (which gates first render below) doesn't wait on the LD
          // network handshake; fresh values stream in and update afterwards.
          {
            sendEventsOnlyForVariation: true,
            wrapperName: 'react-client-sdk',
            bootstrap: 'localStorage',
          },
        );
        ldClientRef.current = ldClient;

        // asyncWithLDProvider skips waitForInitialization when handed a
        // pre-initialized client and snapshots allFlags() immediately — empty
        // at this point — leaving the React context stuck on defaults after
        // login (LD only emits change events for values that change later).
        // Wait for the initial flag payload ourselves. With the localStorage
        // bootstrap this resolves instantly from cache on repeat visits; a
        // timeout or init failure falls through to the passthrough fallback.
        // (The SDK recommends — and warns above — a max timeout of 5s.)
        await ldClient.waitForInitialization(5);

        const Provider = await asyncWithLDProvider({
          clientSideID: process.env.DARKLY_KEY,
          ldClient,
          reactOptions: { useCamelCaseFlagKeys: false },
          // Scope the React flag context to the flags the app reads: useFlags()
          // consumers re-render only on these keys, and each resolves to its
          // registered default instead of undefined until LD delivers a value.
          // New flags MUST be registered in FEATURE_FLAG_DEFAULTS to be
          // visible to useFlags().
          flags: FEATURE_FLAG_DEFAULTS,
        });
        // Functional update because Provider is itself a component.
        if (!cancelled) setLDProvider(() => Provider);
      } catch (error) {
        // LD unreachable (CDN outage, firewall, invalid key, SDK bug). Never
        // leave the app stuck on the loading spinner — fall back to a
        // passthrough provider so children render and flags resolve to their
        // in-code defaults. Allow a retry on the next user-key change.
        // eslint-disable-next-line no-console
        console.error('LaunchDarkly initialization failed:', error);
        initializedKey.current = null;
        if (ldClientRef.current) {
          ldClientRef.current.close();
          ldClientRef.current = null;
        }
        if (!cancelled) setLDProvider(() => PassthroughProvider);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Close the LD client (and its stream) when this provider unmounts.
  useEffect(
    () => () => {
      if (ldClientRef.current) {
        ldClientRef.current.close();
        ldClientRef.current = null;
      }
    },
    [],
  );

  if (!hasToken) return children; // logged out → login page, no flags needed
  if (!LDProvider) return <LoadingSpinner height="100%" position="absolute" />;
  return <LDProvider>{children}</LDProvider>;
};

LaunchDarklyProvider.propTypes = {
  children: PropTypes.node,
};

export default LaunchDarklyProvider;
